import { useMemo } from 'react';

import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { useTour } from '@sorare/core/src/hooks/useTour';
import {
  SaleType,
  TOKENS_STACKED_LIMIT,
  createCardSorts,
} from '@sorare/core/src/lib/algolia';
import { metadatas } from '@sorare/core/src/lib/seo/baseball';

import { MarketRoot } from '@sorare/marketplace/src/components/market/ui';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { secondaryMarketFilters } from '@sorare/marketplace/src/searchCards/mlb/cardFilters';

import { BaseballCardSearch } from 'components/searchCards/BaseballCardSearch';

const SecondaryMarket = () => {
  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();

  const unstacked = useQueryString(SEARCH_PARAMS.UNSTACKED);
  const filters = useDefaultFilters({
    secondary: true,
    onlySettlableCards: true,
  });

  const displayStackedView = !unstacked;
  const mlbSecondaryMarketFilters = useMemo(
    () => secondaryMarketFilters(useMlbSeasonAverageScore),
    [useMlbSeasonAverageScore]
  );

  useTitleAndDescription(
    metadatas.transfers.title,
    metadatas.transfers.description
  );

  const cardSorts = useMemo(
    () =>
      createCardSorts({
        indexPrefix: 'Cards On Sale',
        withBestValue: true,
        withHighestPrice: false,
        withHighestAverageScore: true,
        withPopularPlayer: true,
        withEndingSoon: false,
      }),
    []
  );

  const defaultSort = 'Cards On Sale Popular Player';

  return (
    <MarketRoot>
      <BaseballCardSearch
        sorts={cardSorts}
        defaultSort={defaultSort}
        cardFilters={mlbSecondaryMarketFilters}
        defaultFilters={filters}
        analyticsTags={['TransferMarket', 'Baseball']}
        removeEndedSingleSaleOffers
        filtersTour={useTour(LIFECYCLE.transferMarket, 'filters')}
        cardsTour={useTour(LIFECYCLE.transferMarket, 'cards')}
        distinct={displayStackedView ? TOKENS_STACKED_LIMIT : false}
        stackableTypes={[SaleType.SINGLE_SALE_OFFER]}
      />
    </MarketRoot>
  );
};

export default SecondaryMarket;
